import { useEffect, useState } from "react";
import { Share, Plus, Download, X } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true;

const isIOS = () => {
  const ua = window.navigator.userAgent;
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.maxTouchPoints > 1 && /Macintosh/.test(ua);
  return iOSDevice || iPadOS;
};

const DISMISS_KEY = "mh_install_dismissed";

export const InstallPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    if (ios) {
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [ios]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[92%] max-w-md rounded-2xl border border-[#E5E2DC] bg-white/95 backdrop-blur px-4 py-3 shadow-xl"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2A2626]/10">
          <Download className="h-5 w-5 text-[#2A2626]" />
        </div>
        <div className="flex-1 text-[#2A2626]">
          <p className="text-sm font-semibold">Install this kiosk</p>
          {ios ? (
            <p className="mt-0.5 text-xs leading-relaxed text-[#5C5856]">
              Tap the Share
              <Share className="inline h-3.5 w-3.5 mx-1 -mt-0.5" />
              button, then choose
              <span className="font-medium"> "Add to Home Screen"</span>
              <Plus className="inline h-3.5 w-3.5 mx-1 -mt-0.5" />
              to run full screen.
            </p>
          ) : (
            <p className="mt-0.5 text-xs leading-relaxed text-[#5C5856]">
              Install MinistryHub for a full-screen, Sunday-ready kiosk.
            </p>
          )}
          {!ios && (
            <button
              onClick={install}
              data-testid="install-prompt-button"
              className="mt-2 rounded-full bg-[#2A2626] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#1A1717] transition-colors"
            >
              Install
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="install-prompt-dismiss"
          aria-label="Dismiss"
          className="shrink-0 rounded-full p-1 text-[#5C5856] hover:bg-[#F1EFEA] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
